import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
import { WINDOW_BROKER_CONNECTOR_LOGGER_NAME, getAllowedOrigins } from '../../config';
import { RelayError } from '../../errors';
import { Logger } from 'simple-logging-lib';
import { Subject } from 'reactive-lib/observable/subject';
import { relayOperations } from './operations';
var _instance = /*#__PURE__*/_classPrivateFieldLooseKey("instance");
export class WindowBrokerConnector {
  constructor(windowBroker) {
    this.relayPorts = new Map();
    this.logger = Logger.getLogger(WINDOW_BROKER_CONNECTOR_LOGGER_NAME);
    this.links = new Set();
    this.initialize = () => {
      window.addEventListener('message', this.onWindowMessage);
    };
    this.close = () => {
      window.removeEventListener('message', this.onWindowMessage);
      this.links.forEach(link => link.complete());
      this.relayPorts.forEach(port => {
        port.removeEventListener('message', this.handleOperation);
        port.close();
      });
      this.relayPorts.clear();

      // @ts-expect-error - Resetting the singleton instance for testing purposes
      _classPrivateFieldLooseBase(WindowBrokerConnector, _instance)[_instance] = undefined;
    };
    this.onWindowMessage = event => {
      if (!getAllowedOrigins().includes(event.origin)) {
        return;
      }
      if (relayOperations.probe.match(event.data)) {
        this.handleProbes(event);
      }
      if (relayOperations.handshake.match(event.data)) {
        this.handleHandshake(event).catch(this.logger.error);
      }
    };
    this.handleProbes = event => {
      if (!event.source) {
        this.logger.debug('No source found in probe');
        return;
      }
      const replyTo = event.data.id;
      this.logger.debug(`Replying to probe ID "${replyTo}".`);
      const probeReplyMessage = relayOperations.probeReply({
        replyTo,
        brokerId: this.windowBroker.getBrokerId()
      });
      event.source.postMessage(probeReplyMessage, {
        targetOrigin: event.origin
      });
    };
    this.handleHandshake = async event => {
      const link = new Subject();
      this.links.add(link);
      const requestId = event.data.id;
      const result = await this.windowBroker.handshake(event.data.payload, link).catch(this.logger.error);
      if (!result) {
        return;
      }
      const {
        clientId
      } = result;
      const relayPort = event.ports[0];
      relayPort.start();
      relayPort.addEventListener('message', this.handleOperation);
      this.relayPorts.set(clientId, relayPort);
      relayPort.postMessage(relayOperations.handshakeResponse(Object.assign({}, result, {
        replyTo: requestId
      })));
      link.subscribe(message => relayPort.postMessage(relayOperations.relayMessage(message)));
    };
    this.handleOperation = event => {
      const source = event.target;
      const confirmCompletion = () => {
        if (!source) {
          this.logger.debug('No source found in operation');
          return;
        }
        source.postMessage(relayOperations.opCompletedConfirmation(event.data.id));
      };
      const handleError = error => {
        this.logger.error(error);
        if (!source) {
          this.logger.debug('No source found in operation');
          return;
        }
        source.postMessage(relayOperations.opFailedConfirmation({
          replyTo: event.data.id,
          error
        }));
      };
      if (relayOperations.topicSubscribe.match(event.data)) {
        this.windowBroker.topicSubscribe(event.data.payload).then(confirmCompletion).catch(handleError);
      } else if (relayOperations.topicUnsubscribe.match(event.data)) {
        this.windowBroker.topicUnsubscribe(event.data.payload).then(confirmCompletion).catch(handleError);
      } else if (relayOperations.getTopicSubscriptions.match(event.data)) {
        this.windowBroker.getTopicSubscriptions(event.data.payload).then(subscriptions => {
          const relayPort = this.relayPorts.get(event.data.payload.clientId);
          if (!relayPort) {
            throw new RelayError(RelayError.Reason.GET_TOPIC_SUBS_MISSING_PORT);
          }
          relayPort.postMessage(relayOperations.getTopicSubscriptionsResponse(Object.assign({}, subscriptions, {
            replyTo: event.data.id
          })));
        }).catch(handleError);
      } else if (relayOperations.close.match(event.data)) {
        this.windowBroker.close(event.data.payload.clientId).then(confirmCompletion).then(() => {
          this.close();
        }).catch(handleError);
      } else if (relayOperations.sendMessage.match(event.data)) {
        this.windowBroker.sendMessage(event.data.payload).then(confirmCompletion).catch(handleError);
      }
    };
    this.windowBroker = windowBroker;
  }
  static getInstance(windowBroker) {
    if (!_classPrivateFieldLooseBase(WindowBrokerConnector, _instance)[_instance]) {
      _classPrivateFieldLooseBase(WindowBrokerConnector, _instance)[_instance] = new WindowBrokerConnector(windowBroker);
    }
    return _classPrivateFieldLooseBase(WindowBrokerConnector, _instance)[_instance];
  }
}
Object.defineProperty(WindowBrokerConnector, _instance, {
  writable: true,
  value: void 0
});